import { useState } from "react";
import { Eye, EyeOff, Loader2, ArrowLeft, MailCheck, AlertCircle } from "lucide-react";
import { tt, dir, currentLang } from "@/lib/i18n";
import { useLang } from "@/context/lang-context";

// صفحة الدخول بهوية جسر — تنفيذ تصميم Claude Design «Jisr Login.dc.html»:
// خلفية متدرجة كحلي→تيل + باترن جسر خافت، بطاقة بيضاء مركزية، و4 شاشات:
// تسجيل الدخول / استعادة كلمة المرور / تم الإرسال / لا يوجد حساب.
// النص مُكيَّف مع سلوك الخادم الفعلي: /auth/forgot-password يرسل كلمة مرور
// جديدة بالبريد (وليس رمز تعيين)، ويرجع found لتمييز شاشة «لا يوجد حساب».

interface LoginPageProps {
  onLogin: (agent: { id: number; name: string; email: string; role: string; color: string; avatarUrl: string | null; onlineStatus: string }, token: string) => void;
}

const CARD_STYLE: React.CSSProperties = {
  background: "#FFFFFF",
  borderRadius: 16,
  boxShadow: "0 12px 32px rgba(16,42,67,0.35)",
  padding: "28px 24px 24px",
};

const INPUT_STYLE: React.CSSProperties = {
  height: 42,
  padding: "0 14px",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  fontSize: 14,
  fontFamily: "inherit",
};

const focusOn = (e: React.FocusEvent<HTMLInputElement>) => {
  e.currentTarget.style.borderColor = "#19C37D";
  e.currentTarget.style.boxShadow = "0 0 0 3px rgba(25,195,125,.30)";
};
const focusOff = (e: React.FocusEvent<HTMLInputElement>) => {
  e.currentTarget.style.borderColor = "#E2E8F0";
  e.currentTarget.style.boxShadow = "none";
};

function PrimaryButton({ children, disabled, onClick, type = "button" }: {
  children: React.ReactNode; disabled?: boolean; onClick?: () => void; type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className="w-full font-bold text-white transition-all flex items-center justify-center gap-2"
      style={{
        height: 44, borderRadius: 8, fontSize: 15, background: "#19C37D", border: "none",
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, disabled, onClick }: { children: React.ReactNode; disabled?: boolean; onClick?: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="w-full font-semibold transition-colors hover:bg-slate-50 flex items-center justify-center gap-2"
      style={{
        height: 40, borderRadius: 8, fontSize: 14, background: "transparent", border: "none",
        color: "#0F5B63", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  useLang();
  const [screen, setScreen] = useState<"login" | "forgot" | "sent" | "noemail">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // استعادة كلمة المرور
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [sentEmail, setSentEmail] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || tt("حدث خطأ"));
        return;
      }
      onLogin(data, data.token);
    } catch {
      setError(tt("تعذّر الاتصال بالخادم"));
    } finally {
      setLoading(false);
    }
  }

  async function submitForgot() {
    const em = forgotEmail.trim();
    // البريدُ **أو** اسمُ الدخول (`ara001`): الخادمُ يطابق الاثنين ويرسل إلى بريد الحساب.
    if (!em || /\s/.test(em)) {
      setForgotError(tt("اكتب بريدك الإلكتروني أو اسم المستخدم"));
      return;
    }
    setForgotError("");
    setForgotLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: em }),
      });
      const data = await res.json().catch(() => ({}));
      setSentEmail(typeof data.sentTo === "string" ? data.sentTo : em);
      setScreen(data.found === false ? "noemail" : "sent");
    } catch {
      setForgotError(tt("تعذّر الاتصال بالخادم"));
    } finally {
      setForgotLoading(false);
    }
  }

  const goForgot = () => { setForgotEmail(forgotEmail || email.trim()); setForgotError(""); setScreen("forgot"); };
  const goLogin = () => { setForgotError(""); setScreen("login"); };

  return (
    <div
      dir={dir()}
      className="min-h-screen relative overflow-hidden"
      style={{ background: "linear-gradient(160deg, #102A43 0%, #0F5B63 130%)", fontFamily: "'IBM Plex Sans Arabic', sans-serif" }}
    >
      <style>{`@keyframes jisrPop { 0% { transform: scale(0.6); opacity: 0; } 70% { transform: scale(1.06); } 100% { transform: scale(1); opacity: 1; } }`}</style>
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{ backgroundImage: "url('/brand/jisr-pattern.png')", backgroundSize: "420px", opacity: 0.07 }}
      />

      <div className="relative min-h-screen flex flex-col items-center justify-center px-5 py-10">
        <div className="w-full" style={{ maxWidth: 400 }}>

          {/* ═══ شاشة تسجيل الدخول ═══ */}
          {screen === "login" && (
            <>
              <div className="flex flex-col items-center gap-3 pb-10">
                <img src="/brand/jisr-mark-white.png" alt={tt("جسر")} style={{ height: 64, objectFit: "contain" }} />
                <div style={{ color: "rgba(248,250,252,0.75)", fontSize: 14 }}>{tt("قناة واحدة تصل أعمالك بعملائك")}</div>
              </div>
              <div style={CARD_STYLE} className="flex flex-col gap-[18px]">
                <div className="text-center pt-1" style={{ fontSize: 20, fontWeight: 700, color: "#102A43" }}>{tt("تسجيل الدخول")}</div>

                {error && (
                  <div className="px-3 py-2.5 rounded-lg text-sm" style={{ background: "#FEE2E2", border: "1px solid #FECACA", color: "#B91C1C" }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
                  <div>
                    <label className="block font-semibold mb-1.5" style={{ fontSize: 13, color: "#334155" }}>{tt("البريد الإلكتروني أو اسم المستخدم")}</label>
                    {/* `text` لا `email`: المتصفّح يرفض قيمةً بلا «@» قبل أن
                        تبلغ الخادمَ أصلاً، فاسمُ الدخول القصير لا يُرسَل قطّ. */}
                    <input
                      type="text" dir="ltr" placeholder={tt("name@company.com أو snm011")} autoFocus required
                      value={email} onChange={e => setEmail(e.target.value)}
                      className="w-full outline-none transition-all" style={INPUT_STYLE}
                      onFocus={focusOn} onBlur={focusOff}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <div>
                      <label className="block font-semibold mb-1.5" style={{ fontSize: 13, color: "#334155" }}>{tt("كلمة المرور")}</label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"} dir="ltr" placeholder="••••••••" required
                          value={password} onChange={e => setPassword(e.target.value)}
                          className="w-full outline-none transition-all" style={{ ...INPUT_STYLE, paddingLeft: 40 }}
                          onFocus={focusOn} onBlur={focusOff}
                        />
                        <button
                          type="button" tabIndex={-1} onClick={() => setShowPassword(v => !v)}
                          className="absolute flex items-center justify-center"
                          style={{ left: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#94A3B8", cursor: "pointer", padding: 0 }}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <button
                        type="button" onClick={goForgot}
                        className="transition-colors hover:text-[#19C37D]"
                        style={{ background: "none", border: "none", padding: "4px 0", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: "#0F5B63" }}
                      >
                        {tt("نسيت كلمة المرور؟")}
                      </button>
                    </div>
                  </div>
                  <PrimaryButton type="submit" disabled={loading || !email.trim() || !password}>
                    {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> {tt("جارٍ الدخول...")}</> : (currentLang() === "ar" ? "دخول" : "Sign in")}
                  </PrimaryButton>
                </form>
              </div>

              <a href="/signup" className="block text-center mt-6 text-sm font-semibold" style={{ color: "#19C37D" }}>
                {tt("ما عندك حساب؟ أنشئ شركتك واشترك ←")}
              </a>
              <a href="/attend" className="block text-center mt-3 text-sm font-medium" style={{ color: "rgba(248,250,252,0.65)" }}>
                {tt("موظف؟ سجّل حضورك من هنا ←")}
              </a>
              <div className="text-center" style={{ color: "rgba(248,250,252,0.55)", fontSize: 12, padding: "24px 0 0" }}>
                {tt("جسر · إدارة محادثات الأعمال")}
              </div>
            </>
          )}

          {/* ═══ شاشة استعادة كلمة المرور ═══ */}
          {screen === "forgot" && (
            <>
              <div className="flex items-center gap-2 pb-2">
                <button
                  onClick={goLogin} aria-label={tt("رجوع")}
                  className="flex items-center justify-center transition-colors hover:bg-[rgba(248,250,252,0.16)]"
                  style={{ width: 40, height: 40, borderRadius: 999, border: "1px solid rgba(248,250,252,0.25)", background: "rgba(248,250,252,0.08)", color: "#F8FAFC", cursor: "pointer" }}
                >
                  <ArrowLeft className="h-[18px] w-[18px]" />
                </button>
                <span style={{ color: "rgba(248,250,252,0.75)", fontSize: 13 }}>{tt("العودة لتسجيل الدخول")}</span>
              </div>
              <div className="flex flex-col items-center" style={{ padding: "16px 0 32px" }}>
                <img src="/brand/jisr-mark-white.png" alt={tt("جسر")} style={{ height: 48, objectFit: "contain" }} />
              </div>
              <div style={CARD_STYLE} className="flex flex-col gap-[18px]">
                <div className="flex flex-col gap-1">
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#102A43" }}>{tt("استعادة كلمة المرور")}</div>
                  <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.7 }}>
                    {tt("اكتب بريدك الإلكتروني أو اسم المستخدم وسنرسل كلمة مرور جديدة إلى بريدك المسجّل.")}
                  </div>
                </div>
                <div>
                  <label className="block font-semibold mb-1.5" style={{ fontSize: 13, color: "#334155" }}>{tt("البريد الإلكتروني أو اسم المستخدم")}</label>
                  <input
                    type="text" dir="ltr" placeholder={tt("name@company.com أو snm011")} autoFocus
                    value={forgotEmail} onChange={e => { setForgotEmail(e.target.value); setForgotError(""); }}
                    onKeyDown={e => { if (e.key === "Enter") void submitForgot(); }}
                    className="w-full outline-none transition-all"
                    style={{ ...INPUT_STYLE, ...(forgotError ? { borderColor: "#EF4444" } : {}) }}
                    onFocus={focusOn} onBlur={e => { focusOff(e); if (forgotError) e.currentTarget.style.borderColor = "#EF4444"; }}
                  />
                  {forgotError && <div className="mt-1.5" style={{ fontSize: 12, color: "#EF4444" }}>{forgotError}</div>}
                </div>
                <PrimaryButton disabled={forgotLoading} onClick={() => void submitForgot()}>
                  {forgotLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> {tt("جارٍ الإرسال...")}</> : tt("إرسال")}
                </PrimaryButton>
              </div>
            </>
          )}

          {/* ═══ شاشة النتيجة: تم الإرسال ═══ */}
          {screen === "sent" && (
            <div style={{ ...CARD_STYLE, padding: "36px 24px 24px" }} className="flex flex-col items-center gap-4 text-center">
              <div
                className="flex items-center justify-center"
                style={{ width: 72, height: 72, borderRadius: 999, background: "#E7F8E8", color: "#12A066", animation: "jisrPop 280ms ease-out" }}
              >
                <MailCheck style={{ width: 34, height: 34 }} />
              </div>
              <div className="flex flex-col gap-2">
                <div style={{ fontSize: 20, fontWeight: 700, color: "#102A43" }}>{tt("تم الإرسال ✅")}</div>
                <div style={{ fontSize: 14, color: "#475569", lineHeight: 1.8 }}>
                  {tt("أرسلنا كلمة مرور جديدة إلى")}<br />
                  <span dir="ltr" style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, color: "#0F5B63" }}>{sentEmail}</span>
                </div>
                <div style={{ fontSize: 12, color: "#94A3B8" }}>{tt("تحقق من صندوق الوارد أو مجلد الرسائل غير المرغوبة")}</div>
              </div>
              <div className="flex flex-col gap-2.5 w-full mt-2">
                <PrimaryButton onClick={goLogin}>{tt("العودة لتسجيل الدخول")}</PrimaryButton>
                <GhostButton disabled={forgotLoading} onClick={() => void submitForgot()}>
                  {forgotLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> {tt("جارٍ الإرسال...")}</> : tt("إعادة الإرسال")}
                </GhostButton>
              </div>
            </div>
          )}

          {/* ═══ شاشة النتيجة: لا يوجد بريد ═══ */}
          {screen === "noemail" && (
            <div style={{ ...CARD_STYLE, padding: "36px 24px 24px" }} className="flex flex-col items-center gap-4 text-center">
              <div
                className="flex items-center justify-center"
                style={{ width: 72, height: 72, borderRadius: 999, background: "#FEE2E2", color: "#EF4444", animation: "jisrPop 280ms ease-out" }}
              >
                <AlertCircle style={{ width: 34, height: 34 }} />
              </div>
              <div className="flex flex-col gap-2">
                <div style={{ fontSize: 20, fontWeight: 700, color: "#102A43" }}>{tt("لا يوجد حساب بهذا البريد")}</div>
                <div style={{ fontSize: 14, color: "#475569", lineHeight: 1.8 }}>
                  {tt("البريد")} <span dir="ltr" style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, color: "#EF4444" }}>{sentEmail}</span><br />
                  {tt("غير مسجّل لدينا. تأكد من كتابته بشكل صحيح.")}
                </div>
              </div>
              <div className="flex flex-col gap-2.5 w-full mt-2">
                <PrimaryButton onClick={() => { setForgotError(""); setScreen("forgot"); }}>{tt("جرّب بريداً آخر")}</PrimaryButton>
                <GhostButton onClick={goLogin}>{tt("العودة لتسجيل الدخول")}</GhostButton>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
